import { ReactNode } from "react";
import {
  AppShell,
  Container,
  Group,
  Text,
  Anchor,
  Burger,
  Stack,
  Divider,
  Badge,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, useLocation } from "react-router-dom";
import { SEO } from "../seo/SEO";

interface LayoutProps {
  children: ReactNode;
}

const navigation = [
  { label: "Home", href: "/" },
  { label: "Resume", href: "/resume" },
  { label: "Projects", href: "/projects" },
  { label: "Tabletop Gaming", href: "/tabletop" },
  { label: "Astrophotography", href: "/astrophotography" },
  { label: "Contact", href: "/contact" },
  { label: "Dev Stats", href: "/dev-stats" },
];

const DEFAULT_VERSION = "dev";
const DEFAULT_ENVIRONMENT = "local";
const DEFAULT_BUILD_ID = "local";

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? DEFAULT_VERSION;
const APP_ENVIRONMENT = (
  import.meta.env.VITE_APP_ENVIRONMENT ?? DEFAULT_ENVIRONMENT
).toUpperCase();
const RAW_BUILD_ID = import.meta.env.VITE_APP_BUILD_ID ?? DEFAULT_BUILD_ID;
const BUILD_LABEL =
  RAW_BUILD_ID.length > 12
    ? RAW_BUILD_ID.slice(0, 7).toUpperCase()
    : RAW_BUILD_ID.toUpperCase();
const VERSION_LABEL = APP_VERSION.startsWith("v")
  ? APP_VERSION
  : `v${APP_VERSION}`;

export function Layout({ children }: LayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();

  return (
    <>
      <SEO path={location.pathname} />
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 300,
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header
          style={{
            borderBottom: "1px solid rgba(139, 66, 255, 0.15)",
            backgroundColor: "rgba(6, 6, 10, 0.95)",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
          }}
        >
          <Group h="100%" px="md" justify="space-between">
            <Group gap="sm">
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
              />
              <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
                <Group gap="xs">
                  <img
                    src="/assets/images/design/dach1.png"
                    alt="Dachshund Logo"
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(139, 66, 255, 0.3)",
                      border: "1px solid rgba(139, 66, 255, 0.2)",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                  <div>
                    <Text size="xs" className="text-muted-dark" lh={1}>
                      the
                    </Text>
                    <Text
                      size="md"
                      fw={700}
                      lh={1}
                      className="text-gradient"
                      style={{ letterSpacing: "-0.5px" }}
                    >
                      brianjones.dev
                    </Text>
                  </div>
                </Group>
              </Link>
            </Group>

            <Group gap="lg" visibleFrom="sm">
              {navigation.map((item) => (
                <Anchor
                  key={item.href}
                  component={Link}
                  to={item.href}
                  fw={location.pathname === item.href ? 600 : 500}
                  className={
                    location.pathname === item.href
                      ? "text-primary-dark"
                      : "text-secondary-dark"
                  }
                  td="none"
                  size="sm"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    backgroundColor:
                      location.pathname === item.href
                        ? "rgba(139, 66, 255, 0.2)"
                        : "transparent",
                    border:
                      location.pathname === item.href
                        ? "1px solid rgba(139, 66, 255, 0.3)"
                        : "1px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== item.href) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(139, 66, 255, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== item.href) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {item.label}
                </Anchor>
              ))}
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          p="md"
          style={{
            backgroundColor: "rgba(6, 6, 10, 0.95)",
            borderRight: "1px solid rgba(139, 66, 255, 0.15)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack gap="sm" style={{ flex: 1 }}>
            {navigation.map((item) => (
              <Anchor
                key={item.href}
                component={Link}
                to={item.href}
                fw={location.pathname === item.href ? 600 : 400}
                className={
                  location.pathname === item.href
                    ? "text-primary-dark"
                    : "text-secondary-dark"
                }
                td="none"
                onClick={() => toggle()}
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  backgroundColor:
                    location.pathname === item.href
                      ? "rgba(139, 66, 255, 0.2)"
                      : "transparent",
                  border:
                    location.pathname === item.href
                      ? "1px solid rgba(139, 66, 255, 0.3)"
                      : "1px solid transparent",
                  transition: "all 0.2s ease",
                }}
              >
                {item.label}
              </Anchor>
            ))}
          </Stack>

          <Stack gap="xs" mt="auto">
            <Divider style={{ borderColor: "rgba(139, 66, 255, 0.2)" }} />
            <Group justify="center" py="xs">
              <Badge
                variant="filled"
                size="sm"
                style={{
                  backgroundColor: "#B97F24",
                  color: "white",
                  fontFamily: "monospace",
                  fontSize: "10px",
                  letterSpacing: "0.5px",
                  textTransform: "none",
                }}
              >
                {VERSION_LABEL} · {APP_ENVIRONMENT} · {BUILD_LABEL}
              </Badge>
            </Group>
          </Stack>
        </AppShell.Navbar>

        <AppShell.Main
          style={{
            backgroundColor: "#0a0a0f",
            minHeight: "100vh",
          }}
        >
          <Container size="lg">
            {children}

            <Divider
              my="xl"
              style={{ borderColor: "rgba(139, 66, 255, 0.2)" }}
            />
          </Container>
        </AppShell.Main>
      </AppShell>
    </>
  );
}
